import { GlobalContext, IModalFormElement } from "components/GlobalContext";
import { useContext, useRef, useState } from "react";
import { Table } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCopy, faEnvelope, faQrcode } from "@fortawesome/free-solid-svg-icons";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { copyTextToClipboardAsync, printElement } from "utilities/BrowserUtility";
import { getBaseDomain } from "utilities/MiscUtilities";
import { ReactQrCode } from '@devmehq/react-qr-code';
import MiscService from "services/MiscService";

interface IForm {
    formName: string;
    url: string;
    type: string;
    dynamic: boolean;
    fullUrl: string;
};

const RECORD_COUNT = 20;

const FormDirectoryLookup = () => {
    const globalContext = useContext(GlobalContext);
    const globalContextRef = useRef(globalContext);
    globalContextRef.current = globalContext;
    const [forms, setForms] = useState<IForm[]>([
        {
            formName: "Demo Request",
            url: "/public/demorequest",
            type: "demo",
            dynamic: true,
            fullUrl: `${window.location.protocol}//${getBaseDomain()}/public/demorequest`
        },
        {
            formName: "Register Equipment",
            url: "/public/RegisterEquipment",
            type: "register",
            dynamic: true,
            fullUrl: `${window.location.protocol}//${getBaseDomain()}/public/RegisterEquipment`
        },
        {
            formName: "Register AirCard",
            url: "/public/RegisterAircard",
            type: "aircard",
            dynamic: true,
            fullUrl: `${window.location.protocol}//${getBaseDomain()}/public/RegisterAircard`
        },
        {
            formName: "Health Risk Assessment (North America)",
            url: "/public/hra",
            type: "hra",
            dynamic: true,
            fullUrl: `${window.location.protocol}//${getBaseDomain()}/public/hra`
        },
        {
            formName: "Health Risk Assessment (International)",
            url: "/public/hra/int23",
            type: "hra",
            dynamic: true,
            fullUrl: `${window.location.protocol}//${getBaseDomain()}/public/hra/int23`
        },
        {
            formName: "Info Request",
            url: "/InfoReq",
            type: "InfoReq",
            dynamic: true,
            fullUrl: `${window.location.protocol}//${getBaseDomain()}/InfoReq`
        }
    ]);

    const renderQuickLinks = (form: IForm, baseURL: string, formName: string) => {
        return (
            <span className="quick-link-container">
                <span
                    title="Copy Form URL to clipboard"
                    className="quick-link"
                    onClick={(e) => {
                        buildDynamicUrl(
                            form,
                            (url) => {
                                copyTextToClipboardAsync(baseURL + url)
                                    .then(
                                        (result) => {
                                            if (result) {
                                                window.alert("Copied!");
                                            }
                                            else {
                                                console.log("didn't work");
                                            }
                                        });
                            });
                    }}>
                    <FontAwesomeIcon icon={faCopy as IconProp} />
                </span>
                <span
                    title="Email Form URL"
                    className="quick-link"
                    onClick={async (e) => {

                        buildDynamicUrl(
                            form,
                            async (url, extraData) => {
                                var emailAddress = extraData?.[0].value;

                                if (emailAddress) {
                                    const accessToken = await globalContext.getAccessTokenAsync();

                                    await MiscService.emailFormLinkAsync(
                                        accessToken,
                                        emailAddress,
                                        baseURL + url,
                                        formName);

                                    window.alert("A link to this form has been emailed to " + emailAddress);
                                }
                            },
                            [
                                {
                                    key: "email",
                                    type: "text",
                                    title: `Email Address to send the link to?`,
                                    value: ""
                                }
                            ]);
                    }}>
                    <FontAwesomeIcon icon={faEnvelope as IconProp} />
                </span>
                <span
                    title="Print Form URL QR Code"
                    className="quick-link"
                    onClick={(e) => {
                        buildDynamicUrl(
                            form,
                            (url) => {
                                var formRef = forms.find(f => f.formName === form.formName);
                                if (formRef) {
                                    formRef.fullUrl = baseURL + url;
                                    setForms([...forms]);
                                    setTimeout(() => {
                                        const el = document.getElementById(`qr${form.formName}`);
                                        if (el) {
                                            el.style.display = "";
                                            printElement(el);
                                            el.style.display = "none";
                                        }
                                    }, 1);
                                }
                            });
                    }}>
                    <FontAwesomeIcon icon={faQrcode as IconProp} />
                </span>
                <span
                    className="print-qr-code"
                    id={`qr${form.formName}`}
                    style={{ display: "none" }} >
                    <span><strong>{form.formName}</strong></span><br />
                    <ReactQrCode value={form.fullUrl} />
                </span>
            </span >
        )
    };

    const buildDynamicUrl = (form: IForm, callback: (url: string, extraData?: any[]) => void, customFormElements?: IModalFormElement[]) => {
        var formElements: IModalFormElement[] = [];

        switch (form.type) {
            case "demo":
                formElements = [
                    {
                        key: "r",
                        title: "Rep Code",
                        type: "text",
                        required: false,
                        value: globalContextRef.current.repEntry?.code ?? ""
                    }
                ];
                break;
            case "hra":
                formElements = [
                    {
                        key: "id",
                        title: "Source",
                        type: "text",
                        required: false,
                        value: ""
                    },
                    {
                        key: "r",
                        title: "Rep Code",
                        type: "text",
                        required: false,
                        value: globalContextRef.current.repEntry?.code ?? ""
                    }
                ];
                break;
            case "InfoReq":
                formElements = [
                    {
                        key: "id",
                        title: "Source",
                        type: "text",
                        required: false,
                        value: ""
                    }
                ];
                break;
            case "register":
                formElements = [
                    {
                        key: "serial",
                        title: "Equipment Serial #",
                        type: "text",
                        required: true,
                        value: ""
                    }
                ];
                break;
            case "aircard":
                formElements = [
                    {
                        key: "serial",
                        title: "Equipment Serial #",
                        type: "text",
                        required: true,
                        value: ""
                    }
                ];
                break;
        }

        if (customFormElements) {
            formElements = [...formElements, ...customFormElements];
        }

        globalContext.showModal({
            content:
                <></>,
            title: "Build Link",
            formElements: formElements,
            okCallback:
                async (data) => {
                    var url = form.url;
                    switch (form.type) {
                        case "demo":
                            url += `?r=${data?.[0].value}`;
                            break;
                        case "hra":
                            url += `?id=${data?.[0].value}&r=${data?.[1].value}`;
                            break;
                        case "InfoReq":
                            url += `?id=${data?.[0].value}`;
                            break;
                        case "register":
                            url += `?serial=${data?.[0].value}`;
                            break;
                        case "aircard":
                            url += `?serial=${data?.[0].value}`;
                            break;
                    }
                    var extraData = data?.slice((data?.length ?? 0) - (customFormElements?.length ?? 0), (data?.length ?? 0) + (customFormElements?.length ?? 0));
                    callback(url, extraData);
                },
            cancelCallback: () => { }
        });
    };

    return (
        <>
            <Table
                striped bordered hover size="sm"
                className="lookup-table">
                <tbody>
                    {
                        forms.map(
                            t => {
                                return (
                                    <tr
                                        key={t.formName.replaceAll(" ", "")}
                                        style={{ cursor: "pointer" }}>
                                        <td>
                                            {renderQuickLinks(t, `${window.location.protocol}//${getBaseDomain()}`, t.formName)}&nbsp;
                                            {!t.dynamic && <a href={t.url} target="_blank">{t.formName}</a>}
                                            {t.dynamic &&
                                                <a
                                                    href="javascript:void(0);"
                                                    onClick={() => {
                                                        buildDynamicUrl(
                                                            t,
                                                            (url) => {
                                                                window.open(url, "_blank");
                                                            })
                                                    }}>{t.formName}</a>}
                                        </td>
                                    </tr>
                                );
                            })
                    }
                </tbody>
            </Table>
        </>
    );
}

export default FormDirectoryLookup;
